import { GRADE_GROUPS } from "@/lib/subjects";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ROLE_LABELS } from "@/lib/subjects";
import {
  Users, Plus, Trash2, Upload, X, UserPlus, Download, Search, ShieldCheck, Copy, KeyRound, Pencil,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const ROLES = [
  { key: "ogrenci", label: "Öğrenci" },
  { key: "koc", label: "Koç" },
  { key: "veli", label: "Veli" },
  { key: "admin", label: "Yönetici" },
];

const ROLE_BADGE = {
  admin: "bg-indigo-100 text-indigo-700",
  koc: "bg-sky-100 text-sky-700",
  ogrenci: "bg-emerald-100 text-emerald-700",
  veli: "bg-rose-100 text-rose-700",
};

const emptyForm = { name: "", username: "", password: "", role: "ogrenci", grade: 1, section: "A", parent_of: "", email: "" };

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [roleFilter, setRoleFilter] = useState("");
  const [gradeFilter, setGradeFilter] = useState("");
  const [q, setQ] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [singleOpen, setSingleOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [creds, setCreds] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", password: "", grade: 1, section: "", parent_of: "" });
  const [searchParams] = useSearchParams();

  const students = users.filter((u) => u.role === "ogrenci");

  const load = () => {
    const params = roleFilter ? { role: roleFilter } : {};
    api.get("/users", { params }).then(({ data }) => setUsers(data));
  };
  useEffect(() => { load(); }, [roleFilter]);

  useEffect(() => {
    const u = searchParams.get("edit");
    if (u && users.length) {
      const t = users.find((x) => x.username === u);
      if (t) openEdit(t);
    }
  }, [searchParams, users]);

  const submitSingle = async () => {
    try {
      const payload = { ...form };
      if (payload.role !== "ogrenci") { payload.grade = null; payload.section = null; }
      if (payload.role !== "veli") payload.parent_of = null;
      await api.post("/users", payload);
      toast.success("Kullanıcı eklendi");
      setCreds([{ name: form.name, username: form.username, password: form.password, role: form.role }]);
      setSingleOpen(false);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const submitBulk = async () => {
    const lines = bulkText.split("\n").map((l) => l.trim()).filter(Boolean);
    const parsed = [];
    for (const line of lines) {
      const [name, username, password, role, grade, section] = line.split(",").map((x) => (x || "").trim());
      if (!name || !username || !password || !role) continue;
      parsed.push({
        name, username, password, role,
        grade: role === "ogrenci" && grade ? Number(grade) : null,
        section: role === "ogrenci" ? section || null : null,
      });
    }
    if (!parsed.length) return toast.error("Geçerli satır bulunamadı");
    try {
      const { data } = await api.post("/users/bulk", { users: parsed });
      toast.success(`${data.created_count} kullanıcı eklendi${data.error_count ? `, ${data.error_count} hata` : ""}`);
      if (data.error_count) data.errors.forEach((er) => toast.error(`${er.username}: ${er.error}`));
      const ok = parsed.filter((p) => !(data.errors || []).some((er) => er.username === p.username));
      if (ok.length) setCreds(ok.map((p) => ({ name: p.name, username: p.username, password: p.password, role: p.role })));
      setBulkOpen(false);
      setBulkText("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`${name} silinsin mi?`)) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Silindi");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({ name: u.name || "", password: "", grade: u.grade || 1, section: u.section || "", parent_of: u.parent_of || "" });
  };

  const submitEdit = async () => {
    if (!editForm.name.trim()) return toast.error("Ad soyad gerekli");
    try {
      const payload = { name: editForm.name.trim() };
      if (editForm.password.trim()) payload.password = editForm.password.trim();
      if (editUser.role === "ogrenci") { payload.grade = Number(editForm.grade) || null; payload.section = editForm.section || null; }
      if (editUser.role === "veli") payload.parent_of = editForm.parent_of || null;
      await api.put(`/users/${editUser.id}`, payload);
      toast.success("Kullanıcı güncellendi ✓");
      setEditUser(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/users/bulk-file", fd);
      toast.success(`${data.created_count} kullanıcı eklendi${data.error_count ? `, ${data.error_count} hata` : ""}`);
      if (data.error_count) data.errors.forEach((er) => toast.error(`${er.username}: ${er.error}`));
      if (data.created?.length) setCreds(data.created);
      setBulkOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      e.target.value = "";
    }
  };

  const downloadTemplate = () => {
    const csv = "ad soyad,kullaniciadi,sifre,rol,sinif,sube\nAli Veli,ali,sifre123,ogrenci,1,A\nZeynep Ak,zeynep,sifre123,ogrenci,2,B\nAyla Koc,ayla,sifre123,koc,,\nFatma Ak,fatmaak,sifre123,veli,,\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "kullanici_sablonu.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = users.filter((u) => (!gradeFilter || u.grade === Number(gradeFilter)) && (u.name.toLowerCase().includes(q.toLowerCase()) || u.username.includes(q.toLowerCase())));

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-600">
            <Users className="h-6 w-6" />
          </div>
          <div>
            <h1 className="font-display text-3xl font-extrabold text-slate-900">Kullanıcı Yönetimi</h1>
            <p className="text-slate-500">Öğrenci, koç ve velileri tek tek veya toplu ekleyin.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button data-testid="open-bulk-btn" onClick={() => setBulkOpen(true)} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 font-semibold text-slate-700 shadow-sm transition hover:shadow-md active:scale-95">
            <Upload className="h-5 w-5" /> Toplu Giriş
          </button>
          <button data-testid="open-single-btn" onClick={() => setSingleOpen(true)} className="flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 font-semibold text-white shadow-lg shadow-indigo-200 transition hover:bg-indigo-700 active:scale-95">
            <UserPlus className="h-5 w-5" /> Yeni Kullanıcı
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input data-testid="user-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ara…" className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100" />
        </div>
        <select data-testid="role-filter" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="rounded-2xl border border-slate-200 bg-white px-4 py-3 font-semibold text-slate-700 outline-none focus:border-indigo-400">
          <option value="">Tüm Roller</option>
          {ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
        <select data-testid="grade-filter" value={gradeFilter} onChange={(e) => setGradeFilter(e.target.value)} className="rounded-2xl border border-slate-200 bg-white px-4 py-3 font-semibold text-slate-700 outline-none focus:border-indigo-400">
          <option value="">Tüm Sınıflar</option>
          {[1, 2, 3, 4, 5, 6, 7].map((g) => <option key={g} value={g}>{g}. Sınıf</option>)}
        </select>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-5 py-3.5">Ad Soyad</th>
              <th className="px-5 py-3.5">Kullanıcı Adı</th>
              <th className="px-5 py-3.5">Rol</th>
              <th className="px-5 py-3.5">Sınıf</th>
              <th className="px-5 py-3.5 text-right">İşlem</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.username}`} className="transition hover:bg-slate-50">
                <td className="px-5 py-3.5 font-semibold text-slate-800">
                  <span className="flex items-center gap-2">
                    {u.role === "admin" && <ShieldCheck className="h-4 w-4 text-indigo-500" />}
                    {u.name}
                  </span>
                </td>
                <td className="px-5 py-3.5 font-mono text-slate-500">{u.username}</td>
                <td className="px-5 py-3.5">
                  <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${ROLE_BADGE[u.role]}`}>{ROLE_LABELS[u.role]}</span>
                </td>
                <td className="px-5 py-3.5 text-slate-600">{u.grade ? `${u.grade}. Sınıf${u.section ? ` / ${u.section}` : ""}` : "—"}</td>
                <td className="px-5 py-3.5 text-right">
                  {u.role !== "admin" && (
                    <div className="flex justify-end gap-1">
                      <button data-testid={`edit-user-${u.username}`} onClick={() => openEdit(u)} className="rounded-lg p-2 text-slate-400 transition hover:bg-indigo-50 hover:text-indigo-600 active:scale-90" title="Düzenle">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button data-testid={`delete-user-${u.username}`} onClick={() => remove(u.id, u.name)} className="rounded-lg p-2 text-rose-500 transition hover:bg-rose-50 active:scale-90" title="Sil">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Single user dialog */}
      <Dialog open={singleOpen} onOpenChange={setSingleOpen}>
        <DialogContent className="max-w-lg rounded-2xl">
          <DialogHeader><DialogTitle className="font-display text-2xl">Yeni Kullanıcı</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Ad Soyad"><input data-testid="form-name" value={form.name} onChange={set("name")} className={inputCls} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Kullanıcı Adı"><input data-testid="form-username" value={form.username} onChange={set("username")} className={inputCls} /></Field>
              <Field label="Şifre"><input data-testid="form-password" value={form.password} onChange={set("password")} className={inputCls} /></Field>
            </div>
            <Field label="Rol">
              <select data-testid="form-role" value={form.role} onChange={set("role")} className={inputCls}>
                {ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
            </Field>
            {form.role === "ogrenci" && (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Sınıf">
                  <select data-testid="form-grade" value={form.grade} onChange={set("grade")} className={inputCls}>
                    {GRADE_GROUPS.map((grp) => (
                      <optgroup key={grp.label} label={grp.label}>
                        {grp.grades.map((g) => <option key={g} value={g}>{g}. Sınıf</option>)}
                      </optgroup>
                    ))}
                  </select>
                </Field>
                <Field label="Şube"><input data-testid="form-section" value={form.section} onChange={set("section")} className={inputCls} placeholder="A" /></Field>
              </div>
            )}
            {form.role === "veli" && (
              <Field label="Çocuğu (Öğrenci)">
                <select data-testid="form-parent-of" value={form.parent_of} onChange={set("parent_of")} className={inputCls}>
                  <option value="">Seçiniz…</option>
                  {students.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.grade}. Sınıf)</option>)}
                </select>
              </Field>
            )}
          </div>
          <DialogFooter>
            <button data-testid="submit-single-btn" onClick={submitSingle} className="w-full rounded-xl bg-indigo-600 py-3 font-bold text-white transition hover:bg-indigo-700 active:scale-95">Kaydet</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-2xl rounded-2xl">
          <DialogHeader><DialogTitle className="font-display text-2xl">Toplu Kullanıcı Girişi</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-dashed border-indigo-300 bg-indigo-50/50 p-3">
              <label data-testid="upload-file-label" className="flex cursor-pointer items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-indigo-700 active:scale-95">
                <Upload className="h-4 w-4" /> Excel / CSV Yükle
                <input data-testid="bulk-file-input" type="file" accept=".csv,.xlsx,.xls,.txt" onChange={uploadFile} className="hidden" />
              </label>
              <button data-testid="download-template-btn" onClick={downloadTemplate} className="flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:shadow-sm active:scale-95">
                <Download className="h-4 w-4" /> Şablon İndir
              </button>
              <span className="text-xs text-slate-500">Sütunlar: ad soyad, kullanıcı adı, şifre, rol, sınıf, şube</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-slate-200" />
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">veya elle yapıştır</span>
              <div className="h-px flex-1 bg-slate-200" />
            </div>
            <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-600">
              Her satıra bir kullanıcı. Format: <br />
              <code className="font-mono text-indigo-600">ad soyad, kullaniciadi, sifre, rol, sinif, sube</code><br />
              Rol değerleri: <b>ogrenci</b>, <b>koc</b>, <b>veli</b>. Sınıf/şube sadece öğrenci için.
            </div>
            <textarea
              data-testid="bulk-textarea"
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              rows={8}
              placeholder={"Ali Veli, ali, sifre123, ogrenci, 1, A\nZeynep Ak, zeynep, sifre123, ogrenci, 2, B\nAyla Koç, ayla, sifre123, koc"}
              className="w-full rounded-xl border border-slate-200 bg-white p-3 font-mono text-sm outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
            />
          </div>
          <DialogFooter>
            <button data-testid="submit-bulk-btn" onClick={submitBulk} className="w-full rounded-xl bg-indigo-600 py-3 font-bold text-white transition hover:bg-indigo-700 active:scale-95">İçe Aktar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Kullanıcı düzenleme diyaloğu */}
      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent className="max-w-lg rounded-2xl">
          <DialogHeader><DialogTitle className="font-display text-2xl">Kullanıcıyı Düzenle — {editUser?.username}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Ad Soyad"><input data-testid="edit-name" value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} /></Field>
            <Field label="Yeni Şifre (boş bırakılırsa değişmez)"><input data-testid="edit-password" value={editForm.password} onChange={(e) => setEditForm((f) => ({ ...f, password: e.target.value }))} placeholder="••••••••" className={inputCls} /></Field>
            {editUser?.role === "ogrenci" && (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Sınıf">
                  <select data-testid="edit-grade" value={editForm.grade} onChange={(e) => setEditForm((f) => ({ ...f, grade: e.target.value }))} className={inputCls}>
                    {GRADE_GROUPS.map((grp) => (
                      <optgroup key={grp.label} label={grp.label}>
                        {grp.grades.map((g) => <option key={g} value={g}>{g}. Sınıf</option>)}
                      </optgroup>
                    ))}
                  </select>
                </Field>
                <Field label="Şube"><input data-testid="edit-section" value={editForm.section} onChange={(e) => setEditForm((f) => ({ ...f, section: e.target.value }))} className={inputCls} placeholder="A" /></Field>
              </div>
            )}
            {editUser?.role === "veli" && (
              <Field label="Çocuğu (Öğrenci)">
                <select data-testid="edit-parent-of" value={editForm.parent_of} onChange={(e) => setEditForm((f) => ({ ...f, parent_of: e.target.value }))} className={inputCls}>
                  <option value="">Seçiniz…</option>
                  {students.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.grade}. Sınıf)</option>)}
                </select>
              </Field>
            )}
          </div>
          <DialogFooter>
            <button data-testid="submit-edit-btn" onClick={submitEdit} className="w-full rounded-xl bg-indigo-600 py-3 font-bold text-white transition hover:bg-indigo-700 active:scale-95">Değişiklikleri Kaydet</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Giriş bilgileri diyaloğu — kimlikleri admin dağıtır */}
      <Dialog open={!!creds} onOpenChange={(o) => !o && setCreds(null)}>
        <DialogContent className="max-w-lg rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-2xl"><KeyRound className="h-6 w-6 text-indigo-600" /> Giriş Bilgileri</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">Bu bilgileri öğrenci/veliye iletin. Şifreler yalnızca şimdi gösterilir.</p>
          <div data-testid="creds-list" className="max-h-80 space-y-2 overflow-y-auto">
            {(creds || []).map((c) => (
              <div key={c.username} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="min-w-0">
                  <div className="truncate font-semibold text-slate-800">{c.name} <span className="ml-1 rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-bold text-indigo-700">{ROLE_LABELS[c.role] || c.role}</span></div>
                  <div className="font-mono text-sm text-slate-600">{c.username} · {c.password}</div>
                </div>
                <button data-testid={`copy-cred-${c.username}`} onClick={() => copyText(`Kullanıcı adı: ${c.username}\nŞifre: ${c.password}`)} className="shrink-0 rounded-lg p-2 text-slate-500 transition hover:bg-white hover:text-indigo-600" title="Kopyala">
                  <Copy className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <button data-testid="copy-all-creds-btn" onClick={() => copyText((creds || []).map((c) => `${c.name} — Kullanıcı adı: ${c.username} · Şifre: ${c.password}`).join("\n"))} className="w-full rounded-xl bg-indigo-600 py-3 font-bold text-white transition hover:bg-indigo-700 active:scale-95">
              Tümünü Kopyala
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const copyText = async (t) => {
  try { await navigator.clipboard.writeText(t); toast.success("Kopyalandı ✓"); }
  catch { toast.error("Kopyalanamadı"); }
};

const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100";

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-semibold text-slate-700">{label}</label>
      {children}
    </div>
  );
}
